import { Fragment } from "react";
import { Link } from "react-router-dom";
import { ChevronRightIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { RESOURCE_LABELS, toNumber, type ActivityOrganisation, type Granularity } from "./activityResources";
import { RelativeTime } from "./RelativeTime";
import { ResourceDetailTable } from "./ResourceDetailTable";

const BASE_COLUMNS = 7;

/**
 * Organisations against one period: what each created in it, all-time, and when it was last active. A row opens
 * to the per-resource breakdown. Mounted globally (with a Client column) or under a client (without).
 */
export function ActivityOrganisationsTable({
  organisations,
  periodStart,
  periodLabel,
  granularity,
  showClient,
  expanded,
  onExpand,
}: {
  organisations: ActivityOrganisation[];
  periodStart: string;
  periodLabel: string;
  granularity: Granularity;
  showClient: boolean;
  expanded: string | null;
  onExpand: (organisationId: string | null) => void;
}) {
  const columnCount = BASE_COLUMNS + (showClient ? 1 : 0);

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-8" />
          <TableHead>Organisation</TableHead>
          <TableHead>ID</TableHead>
          {showClient && <TableHead>Client</TableHead>}
          <TableHead className="text-right">{periodLabel}</TableHead>
          <TableHead>Breakdown</TableHead>
          <TableHead className="text-right">All time</TableHead>
          <TableHead>Last active</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {organisations.length === 0 ? (
          <TableRow>
            <TableCell colSpan={columnCount} className="text-muted-foreground py-8 text-center">
              No organisation created anything in this {granularity === "months" ? "month" : "week"}.
            </TableCell>
          </TableRow>
        ) : (
          organisations.map((org) => {
            const period = (granularity === "months" ? org.months : org.weeks).find((p) => p.start === periodStart);
            const inPeriod = toNumber(period?.total);
            const isOpen = expanded === org.organisationId;
            return (
              <Fragment key={org.organisationId}>
                <TableRow
                  className="cursor-pointer"
                  data-state={isOpen ? "selected" : undefined}
                  onClick={() => onExpand(isOpen ? null : org.organisationId)}
                >
                  <TableCell>
                    <button
                      type="button"
                      aria-expanded={isOpen}
                      aria-label={`${isOpen ? "Hide" : "Show"} breakdown for ${org.organisationName}`}
                      className="text-muted-foreground hover:text-foreground flex"
                    >
                      <ChevronRightIcon className={cn("size-4 transition-transform", isOpen && "rotate-90")} />
                    </button>
                  </TableCell>
                  <TableCell>
                    <Link
                      to={`/organisations/${org.organisationId}`}
                      onClick={(e) => e.stopPropagation()}
                      className="font-medium hover:underline"
                    >
                      {org.organisationName}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Link to={`/organisations/${org.organisationId}`} onClick={(e) => e.stopPropagation()}>
                      <Badge variant="secondary" className="font-mono text-xs">
                        {org.organisationId.slice(0, 8)}...
                      </Badge>
                    </Link>
                  </TableCell>
                  {showClient && (
                    <TableCell>
                      <Link
                        to={`/clients/${org.clientId}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-muted-foreground hover:text-foreground hover:underline"
                      >
                        {org.clientName}
                      </Link>
                    </TableCell>
                  )}
                  <TableCell className={cn("text-right tabular-nums", inPeriod === 0 && "text-muted-foreground")}>
                    {inPeriod === 0 ? "-" : inPeriod.toLocaleString("en-GB")}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(period?.counts ?? []).map((count) => (
                        <Badge key={count.resource} variant="outline" className="text-xs font-normal">
                          {RESOURCE_LABELS[count.resource]} {toNumber(count.count).toLocaleString("en-GB")}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{toNumber(org.total).toLocaleString("en-GB")}</TableCell>
                  <TableCell className="whitespace-nowrap">
                    <RelativeTime value={org.lastActiveAt} />
                  </TableCell>
                </TableRow>
                {isOpen && (
                  <TableRow className="hover:bg-transparent">
                    <TableCell />
                    <TableCell colSpan={columnCount - 1} className="pb-4">
                      <ResourceDetailTable allTime={org.resources} period={period} periodLabel={periodLabel} />
                      {org.firstActivityAt && (
                        <p className="text-muted-foreground mt-2 text-xs">
                          First activity <RelativeTime value={org.firstActivityAt} />
                        </p>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })
        )}
      </TableBody>
    </Table>
  );
}
